<script>
  import ProjectTabs from "../components/ProjectTabs.svelte";
  import { onMount } from 'svelte';
  import { questionsetStore } from '../stores/questionsetStore';
  import AddQuestionModal from '../components/questionSetEditor/AddQuestionModal.svelte';
  import QuestionEditPanel from '../components/questionSetEditor/QuestionEditPanel.svelte';
  import SectionEditModal from '../components/questionSetEditor/SectionEditModal.svelte';
  import QuestionSetVisualiser from "../components/questionSetEditor/QuestionSetVisualiser.svelte";
  import MapSelector from '../components/questionSetEditor/MapSelector.svelte';
  import { getProject, updateProject } from "../services/project";
  import MultiMapsEditor from "../components/questionSetEditor/configuration/MultiMapsEditor.svelte";
  import StylingConfigEditor from "../components/questionSetEditor/configuration/StylingConfigEditor.svelte";
  import ExitSetEditor from "../components/questionSetEditor/configuration/ExitSetEditor.svelte";
  import DelRelDisplay from "../components/questionSetEditor/configuration/DelRelDisplay.svelte";
  
  export let projectId;
  
  // Local state
  let selectedItem = null;
  let showAddQuestionModal = false;
  let showAddSectionModal = false;
  let showSectionEditModal = false;
  let showMapSelector = false;
  let editingSectionId = null;
  let saveStatus = { type: 'success', message: 'All valid ✓', timestamp: 'not saved yet' };
  let isLoading = true;
  
  // NEW: Collapsible config state
  let configExpanded = false;
  
  // Draft question management - questions not yet in the store
  let draftQuestion = null;
  let isDraftMode = false;
  
  // Reordering state
  let isReorderMode = false;
  let reorderData = null;
  let draggedItem = null;
  let dragOverIndex = -1;
  
  // Reactive data from store
  $: data = $questionsetStore;

  onMount(async () => {
    try {
      const project = await getProject(projectId);
      
      if (project && project.questionSet) {
        const questionsetData = ensureBasicConfig(project.questionSet);
        questionsetStore.importJson(questionsetData);
      } else {
        const emptyQuestionset = createDefaultQuestionset(project);
        questionsetStore.importJson(emptyQuestionset);
      }
      
      isLoading = false;
    } catch (error) {
      console.error('Failed to load questionset:', error);
      saveStatus = { 
        type: 'error', 
        message: 'Failed to load data', 
        timestamp: new Date().toLocaleTimeString()
      };
      isLoading = false;
    }
  });

  function ensureBasicConfig(questionset) {
    const defaultConfig = {
      projectFlag: "sm",
      publicProject: true,
      published: false,
      updateable: true,
      multiAnswers: false,
      multiRings: false,
      mapOn: false,
      timeline: false,
      tour: false,
      explore: false,
      workspace: false,
      filterActions: [],
      filterBoxes: [],
      lenseActions: [],
      filterBoxTitles: [],
      linkActions: [],
      translations: {},
      extraMarkup: "",
      delRel: [],
      multiMaps: [],
      colourChanges: {},
    };
    
    return {
      ...questionset,
      config: {
        ...defaultConfig,
        ...questionset.config
      },
      sections: questionset.sections || {}
    };
  }
  
  function createDefaultQuestionset(project) {
    return {
      labelOrganisation: project?.organisation || "Organisation",
      labelProject: project?.name || "Project",
      sections: {
        intro: {
          title: "Welcome",
          text: "Welcome to our questionnaire. Please answer the following questions."
        },
        outro: {
          title: "Thank You",
          text: "Thank you for completing our questionnaire."
        }
      },
      questions: [],
      config: {
        projectFlag: "sm",
        publicProject: true,
        published: false,
        updateable: true,
        multiAnswers: false,
        multiRings: false,
        mapOn: false,
        timeline: false,
        tour: false,
        explore: false,
        workspace: false,
        filterActions: [],
        filterBoxes: [],
        lenseActions: [],
        filterBoxTitles: [],
        linkActions: [],
        translations: {},
        extraMarkup: "",
        delRel: [],
        multiMaps: [],
        colourChanges: {}
      }
    };
  }
  
  // Helper functions
  function getRelationTypeFromDataset(dataset) {
    if (!dataset || !Array.isArray(dataset)) return "unknown";
    
    if (dataset.some((item) => item[0] === "setSelf")) return "selfNode";
    if (dataset.some((item) => item[0] === "nodes")) return "createNodes";
    if (dataset.some((item) => item[0] === "nodeProps")) return "addProps";
    if (dataset.some((item) => item[0] === "selfProps" && !dataset.some(i => i[0] === "setSelf"))) return "selfProps";
    
    return "unknown";
  }

  function isPropsQuestion(question) {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset[0][0] === "selfProps" || question.dataset[0][0] === "nodeProps";
  }

  function getQuestionSummary(question) {
    let summary = [];
    
    summary.push(`Required: ${question.allowEmpty === false || question.allowEmpty === undefined ? 'Yes' : 'No'}`);
    
    if (question.choices) summary.push(`Choices: ${question.choices.length}`);
    if (question.suggestions) summary.push(`Suggestions: ${Array.isArray(question.suggestions) ? question.suggestions.length : 'Dynamic'}`);
    if (question.maxSelected) summary.push(`Max: ${question.maxSelected}`);
    if (question.minSelected) summary.push(`Min: ${question.minSelected}`);
    if (question.fieldLabels) {
      if (Array.isArray(question.fieldLabels)) {
        summary.push(`Fields: ${question.fieldLabels.length}`);
      } else {
        summary.push(`Fields: ${question.fieldLabels}`);
      }
    }
    if (question.manyToMany) summary.push('Many-to-Many');
    if (question.extendable) summary.push('Extendable');
    if (question.other) summary.push('Has Other');
    
    summary.push(`Type: ${getRelationTypeFromDataset(question.dataset)}`);
    
    return summary.join(' | ');
  }

  // NEW: Toggle config visibility
  function toggleConfig() {
    configExpanded = !configExpanded;
  }
  
  // Configuration updates 
  function updateConfig(field, value) {
    questionsetStore.update(currentData => {
      const newData = { ...currentData };
      newData.config = { ...newData.config, [field]: value };
      
      // Handle tour functionality
      if (field === 'tour' && value === true) {
        // Add tourText to all questions that don't have it
        newData.questions = newData.questions.map((question, index) => {
          if (!question.tourText) {
            return {
              ...question,
              tourText: `In question ${index + 1} this was the data added`
            };
          }
          return question;
        });
      } /*else if (field === 'tour' && value === false) {
        // Remove tourText from all questions
        newData.questions = newData.questions.map(question => {
          const { tourText, ...questionWithoutTour } = question;
          return questionWithoutTour;
        });
      }*/
      
      return newData;
    });
  }
  
  function updateGeneral(field, value) {
    questionsetStore.update(currentData => ({
      ...currentData,
      [field]: value
    }));
  }

  function toggleMapInQuestionset() {
    console.log('clicked toggle map in question set');
    questionsetStore.update(currentData => {
      const newData = { ...currentData };
      
      if (newData.map) {
        // Remove map - create new object without map property
        const { map, ...dataWithoutMap } = newData;
        return dataWithoutMap;
      } else {
        // Add default map
        return {
          ...newData,
          map: {
            lat: 52.5200, // Berlin
            long: 13.4050, // Berlin
            radiusKm: 0.25
          }
        };
      }
    });
    console.log('questionsetStore', $questionsetStore);
  }

  function updateMapData(mapData) {
    // Map data is now updated directly in the MapSelector component
    // This function is kept for compatibility but the MapSelector handles the store update
    console.log('Map updated:', mapData);
  }

  function showMapEditor() {
    showMapSelector = true;
  }

  function handleMapSave(event) {
    // The MapSelector component handles the store update directly
    showMapSelector = false;
  }
  
  // Section functions
  function addSection() {
    showAddSectionModal = true;
  }
  
  function handleAddSection(id, title, text) {
    questionsetStore.updateSection(id, title, text);
    showAddSectionModal = false;
  }
  
  function editSection(sectionId) {
    editingSectionId = sectionId;
    showSectionEditModal = true;
  }
  
  function handleUpdateSection(sectionId, title, text) {
    questionsetStore.updateSection(sectionId, title, text);
    showSectionEditModal = false;
  }
  
  function removeSection(id) {
    if (confirm(`Remove section "${id}"?`)) {
      questionsetStore.removeSection(id);
    }
  }
  
  // Question functions
  function addQuestion() {
    showAddQuestionModal = true;
  }
  
  function handleCreateDraft(event) {
    const { type, relationshipType } = event.detail;
    
    // Create draft question template
    let newQuestion = {
      id: `question--${data.questions.length}`,
      kind: type,
      label: `New ${type} Question`,
      allowEmpty: true,
      encrypt: ["project", "consent"]
    };

    // Add tourText if tour is enabled
    if (data.config?.tour && !newQuestion.tourText) {
      newQuestion.tourText = `In question ${data.questions.length + 1} this was the data added`;
    }
    
    // Add dataset based on relationship type
    if (relationshipType === 'selfNode') {
      newQuestion.dataset = [
        ["setSelf"],
        ["selfProps", { "type": "type-name", "survey": "started" }]
      ];
    } else if (relationshipType === 'createNodes') {
      newQuestion.dataset = [
        ["nodes", { "type": "node-type", "selector": "node-selector" }],
        ["links", [{ "source": "__self__", "target": "__answers__", "type": "link-type" }]]
      ];
    } else if (relationshipType === 'selfProps') {
      newQuestion.dataset = [
        ["selfProps", { "prop-name": "__answers__" }]
      ];
    } else if (relationshipType === 'addProps') {
      newQuestion.dataset = [
        ["nodeProps", 0, { "prop-name": "__answers__" }]
      ];
    }
    
    // Add type-specific properties
    if (type === 'CheckBox') {
      newQuestion.choices = ["Choice 1", "Choice 2", "Choice 3"];
      newQuestion.minSelected = 1;
      newQuestion.maxSelected = 3;
    } else if (type === 'Select') {
      newQuestion.suggestions = ["Option 1", "Option 2", "Option 3"];
    } else if (type === 'Text') {
      if (relationshipType === 'createNodes') {
        newQuestion.fieldLabels = ["", "", ""];
        newQuestion.suggestions = ["__datasetNodeMatches__"];
      }
    } else if (type === 'Quant') {
      newQuestion.minValue = 0;
      newQuestion.maxValue = 10;
      newQuestion.questionLabels = ["Question 1"];
    } else if (type === 'Map') {
      newQuestion.helpText = "Drag the pin to mark that place";
      newQuestion.currentLocation = true;
    }
    
    // Set up draft mode
    draftQuestion = newQuestion;
    isDraftMode = true;
    showAddQuestionModal = false;
    
    // Open edit panel for draft
    selectedItem = { type: 'question', isDraft: true };
  }
  
  function handleSaveDraft(event) {
    const { question } = event.detail;
    
    // Add the completed question to the store
    try {
      questionsetStore.addQuestion(question);
      
      // Clear draft state
      draftQuestion = null;
      isDraftMode = false;
      selectedItem = null;
    } catch (error) {
      alert(`Error adding question: ${error.message}`);
    }
  }
  
  function handleCancelDraft() {
    // Just clear draft state
    draftQuestion = null;
    isDraftMode = false;
    selectedItem = null;
  }
  
  function editQuestion(index) {
    selectedItem = { type: 'question', index, isDraft: false };
  }
  
  function handleUpdateQuestion(event) {
    // Question already updated in store, just close panel
    selectedItem = null;
  }
  
  function removeQuestion(index) {
    if (confirm('Remove this question?')) {
      try {
        questionsetStore.removeQuestion(index);
        selectedItem = null;
      } catch (error) {
        alert(`Error removing question: ${error.message}`);
      }
    }
  }

  // Reordering functions
  function enterReorderMode() {
    reorderData = JSON.parse(JSON.stringify(data));
    isReorderMode = true;
  }

  function exitReorderMode() {
    isReorderMode = false;
    reorderData = null;
    draggedItem = null;
    dragOverIndex = -1;
  }

  function saveReorder() {
    if (!reorderData) return;

    const updatedData = applyReorderIdentifiers(reorderData);
    questionsetStore.set(updatedData);
    
    exitReorderMode();
  }

  function cancelReorder() {
    exitReorderMode();
  }

  function applyReorderIdentifiers(reorderedData) {
    // Create completely new data structure to ensure reactivity
    const newData = JSON.parse(JSON.stringify(reorderedData));
    const newSections = {};
    const newQuestions = [];
    
    const reorderedContent = getOrderedContent(newData);
    let questionIndex = 0;
    
    reorderedContent.forEach((item, index) => {
      if (item.type === 'section') {
        if (item.id === 'intro' || item.id === 'outro') {
          newSections[item.id] = { ...item.data };
        } else {
          const nextQuestionIndex = findNextQuestionIndex(reorderedContent, index);
          if (nextQuestionIndex !== -1) {
            const newSectionId = `question--${nextQuestionIndex}`;
            newSections[newSectionId] = { ...item.data };
          }
        }
      } else if (item.type === 'question') {
        const newQuestion = {
          ...item.data,
          id: `question--${questionIndex}`
        };
        
        if (newQuestion.dataset) {
          newQuestion.dataset = updateDatasetReferences(
            JSON.parse(JSON.stringify(newQuestion.dataset)), 
            reorderedData.questions, 
            newQuestions
          );
        }
        
        newQuestions.push(newQuestion);
        questionIndex++;
      }
    });
    
    return {
      ...newData,
      questions: newQuestions,
      sections: newSections
    };
  }

  function findNextQuestionIndex(orderedContent, currentIndex) {
    for (let i = currentIndex + 1; i < orderedContent.length; i++) {
      if (orderedContent[i].type === 'question') {
        let questionCount = 0;
        for (let j = 0; j < i; j++) {
          if (orderedContent[j].type === 'question') {
            questionCount++;
          }
        }
        return questionCount;
      }
    }
    return -1;
  }

  function updateDatasetReferences(dataset, oldQuestions, newQuestions) {
    if (!Array.isArray(dataset)) return dataset;
    
    return dataset.map(handler => {
      if (!Array.isArray(handler)) return handler;
      
      const [handlerType, ...params] = handler;
      
      if (handlerType === 'nodeProps' && typeof params[0] === 'number') {
        const oldQuestionIndex = params[0];
        const oldQuestion = oldQuestions[oldQuestionIndex];
        
        if (oldQuestion) {
          const newIndex = newQuestions.findIndex(q => 
            q.label === oldQuestion.label && 
            JSON.stringify(q.dataset) === JSON.stringify(oldQuestion.dataset)
          );
          
          if (newIndex !== -1) {
            return [handlerType, newIndex, ...params.slice(1)];
          }
        }
      }
      
      return handler;
    });
  }

  // Drag and drop functions
  function handleDragStart(event, item, index) {
    if (!isReorderMode) return;
    
    draggedItem = { item, index };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/html', event.target.outerHTML);
  }

  function handleDragOver(event, index) {
    if (!isReorderMode || !draggedItem) return;
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    dragOverIndex = index;
  }

  function handleDragLeave(event) {
    if (!isReorderMode) return;
    dragOverIndex = -1;
  }

  function handleDrop(event, targetIndex) {
    if (!isReorderMode || !draggedItem) return;
    
    event.preventDefault();
    
    const sourceIndex = draggedItem.index;
    if (sourceIndex === targetIndex) {
      draggedItem = null;
      dragOverIndex = -1;
      return;
    }

    const orderedContent = getOrderedContent(reorderData);
    
    const draggedItemData = orderedContent[sourceIndex];
    if (draggedItemData.type === 'section' && 
        (draggedItemData.id === 'intro' || draggedItemData.id === 'outro')) {
      draggedItem = null;
      dragOverIndex = -1;
      return;
    }

    const newOrderedContent = [...orderedContent];
    const [movedItem] = newOrderedContent.splice(sourceIndex, 1);
    newOrderedContent.splice(targetIndex, 0, movedItem);

    reorderData = reconstructDataFromOrderedContent(newOrderedContent, reorderData);
    
    draggedItem = null;
    dragOverIndex = -1;
  }

  function reconstructDataFromOrderedContent(orderedContent, originalData) {
    const newQuestions = [];
    const newSections = {};
    
    orderedContent.forEach(item => {
      if (item.type === 'question') {
        newQuestions.push(item.data);
      } else if (item.type === 'section') {
        newSections[item.id] = item.data;
      }
    });
    
    return {
      ...originalData,
      questions: newQuestions,
      sections: newSections
    };
  }
  
  // Save function
  async function saveChanges() {
    try {
      const questionSetData = questionsetStore.exportJson();

      // Calculate and add delRel to the data before saving
      const calculatedDelRel = calculateDelRel(questionSetData.questions || []);
      questionSetData.config = {
        ...questionSetData.config,
        delRel: calculatedDelRel
      };
      
      const project = await getProject(projectId);
      const updatedProject = {
        ...project,
        questionSet: questionSetData
      };
      
      const result = await updateProject(updatedProject);
      
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors.join(', '));
      }
      
      saveStatus = { 
        type: 'success', 
        message: 'Saved successfully ✓', 
        timestamp: new Date().toLocaleTimeString()
      };
    } catch (error) {
      console.error('Save error:', error);
      saveStatus = { 
        type: 'error', 
        message: 'Save failed: ' + error.message, 
        timestamp: new Date().toLocaleTimeString()
      };
    }
  }
  
  function getOrderedContent(dataSource = data) {
    if (!dataSource) return [];
    
    const content = [];
    
    if (dataSource.sections?.intro) {
      content.push({ type: 'section', id: 'intro', data: dataSource.sections.intro });
    }
    
    dataSource.questions?.forEach((question, index) => {
      if (dataSource.sections?.[question.id]) {
        content.push({ type: 'section', id: question.id, data: dataSource.sections[question.id] });
      }
      
      content.push({ type: 'question', index, data: question });
    });
    
    if (dataSource.sections?.outro) {
      content.push({ type: 'section', id: 'outro', data: dataSource.sections.outro });
    }
    
    if (dataSource.sections) {
      Object.entries(dataSource.sections).forEach(([id, section]) => {
        if (id !== 'intro' && id !== 'outro' && !id.startsWith('question--')) {
          content.push({ type: 'section', id, data: section });
        }
      });
    }
    
    return content;
  }

  function calculateDelRel(questions) {
  if (!questions || !Array.isArray(questions)) return [];
  
  const delRel = [];
  
  // Helper function to check if a question is a self node
  function isSelfNode(question) {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset.some(handler => Array.isArray(handler) && handler[0] === 'setSelf');
  }
  
  // Helper function to extract question references from various dataset patterns
  function getQuestionReferences(targetQuestionIndex, questions) {
    const references = new Set();
    
    // Check all questions that come after the target question
    for (let i = targetQuestionIndex + 1; i < questions.length; i++) {
      const checkingQuestion = questions[i];
      
      if (!checkingQuestion.dataset || !Array.isArray(checkingQuestion.dataset)) continue;
      
      // Check each handler in the dataset
      for (const handler of checkingQuestion.dataset) {
        if (!Array.isArray(handler)) continue;
        
        const [handlerType, ...params] = handler;
        
        // Check nodeProps references
        if (handlerType === 'nodeProps') {
          const [referencedIndex] = params;
          if (referencedIndex === targetQuestionIndex) {
            references.add(i);
          }
        }
        
        // Check links for answer references
        if (handlerType === 'links') {
          const linksArray = params[0];
          if (Array.isArray(linksArray)) {
            
            // Handle both single link objects and arrays of links
            const links = Array.isArray(linksArray[0]) ? linksArray : [linksArray];
            
            for (const linkGroup of links) {
              const linksToCheck = Array.isArray(linkGroup) ? linkGroup : [linkGroup];
              
              for (const link of linksToCheck) {
                if (typeof link === 'object' && link !== null) {
                  // Check source references
                  if (typeof link.source === 'string') {
                    const sourcePatterns = [
                      `__answer${targetQuestionIndex}__`,
                      `__answers${targetQuestionIndex}__`,
                      `__manyAnswer${targetQuestionIndex}__`,
                      `__manyAnswers${targetQuestionIndex}__`
                    ];
                    
                    if (sourcePatterns.some(pattern => link.source === pattern)) {
                      references.add(i);
                    }
                  }
                  
                  // Check target references
                  if (typeof link.target === 'string') {
                    const targetPatterns = [
                      `__answer${targetQuestionIndex}__`,
                      `__answers${targetQuestionIndex}__`,
                      `__manyAnswer${targetQuestionIndex}__`,
                      `__manyAnswers${targetQuestionIndex}__`
                    ];
                    
                    if (targetPatterns.some(pattern => link.target === pattern)) {
                      references.add(i);
                    }
                  }
                }
              }
            }
          }
        }
        
        // Check nested links (many links)
        if (handlerType === 'manyLinks') {
          const manyLinksArray = params[0];
          if (Array.isArray(manyLinksArray)) {
            for (const nestedLinks of manyLinksArray) {
              if (Array.isArray(nestedLinks)) {
                for (const link of nestedLinks) {
                  if (typeof link === 'object' && link !== null) {
                    // Check source references in nested links
                    if (typeof link.source === 'string') {
                      const sourcePatterns = [
                        `__answer${targetQuestionIndex}__`,
                        `__answers${targetQuestionIndex}__`,
                        `__manyAnswer${targetQuestionIndex}__`,
                        `__manyAnswers${targetQuestionIndex}__`
                      ];
                      
                      if (sourcePatterns.some(pattern => link.source === pattern)) {
                        references.add(i);
                      }
                    }
                    
                    // Check target references in nested links
                    if (typeof link.target === 'string') {
                      const targetPatterns = [
                        `__answer${targetQuestionIndex}__`,
                        `__answers${targetQuestionIndex}__`,
                        `__manyAnswer${targetQuestionIndex}__`,
                        `__manyAnswers${targetQuestionIndex}__`
                      ];
                      
                      if (targetPatterns.some(pattern => link.target === pattern)) {
                        references.add(i);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return Array.from(references);
  }
  
  // Calculate delRel for each question
  for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
    const question = questions[questionIndex];
    
    // Self nodes don't delete anything (shouldn't be deleted without deleting everything)
    if (isSelfNode(question)) {
      delRel.push([questionIndex, null]);
      continue;
    }
    
    // Get all references to this question from later questions
    const references = getQuestionReferences(questionIndex, questions);
    
    if (references.length === 0) {
      // No references found
      delRel.push([questionIndex, null]);
    } else if (references.length === 1) {
      // Single reference
      delRel.push([questionIndex, references[0]]);
    } else {
      // Multiple references
      delRel.push([questionIndex, references]);
    }
  }
  
  return delRel;
}

// Function for manual recalculation (for the display component button)
function recalculateDelRel() {
  // This just forces a reactive update to show current delRel calculation
  // The actual calculation happens in the reactive statement below
  data = data; // Force reactive update
}

// Add this reactive statement to automatically calculate delRel for display
$: currentDelRel = calculateDelRel(data?.questions || []);


  $: orderedContent = getOrderedContent(isReorderMode ? reorderData : data);
</script>

<ProjectTabs {projectId} />
<div class="panel">
  <div class="questionset-editor">
    
    {#if isLoading}
      <div class="loading-state">
        <h2>Loading questionset...</h2>
      </div>
    {:else}
      
      <!-- Main Content Area -->
      <div class="main-content">
        
        <!-- Global Configuration -->
        <div class="global-config" class:collapsed={!configExpanded}>
          <div class="config-header" on:click={toggleConfig}>
            <div class="config-title">
              <h1>Project Configuration</h1>
              <span class="config-summary">
                {data.labelOrganisation || 'No organisation'} / {data.labelProject || 'No project name'}
                {#if data && data.map !== undefined}• Map enabled{/if}
                {#if data.config?.tour}• Tour enabled{/if}
              </span>
            </div>
            <button class="toggle-btn" class:expanded={configExpanded}>
              {configExpanded ? '▼' : '▶'}
            </button>
          </div>
          
          {#if configExpanded}
            <div class="config-content">
              <div class="config-basic">
                <div class="field-wrapper">
                  <label>Organisation</label>
                  <input 
                    type="text"
                    value={data.labelOrganisation || ''}
                    on:input={(e) => updateGeneral('labelOrganisation', e.target.value)}
                    disabled={isReorderMode}
                  />
                </div>
                <div class="field-wrapper">
                  <label>Project Name</label>
                  <input 
                    type="text"
                    value={data.labelProject || ''}
                    on:input={(e) => updateGeneral('labelProject', e.target.value)}
                    disabled={isReorderMode}
                  />
                </div>
              </div>
              
              <div class="config-grid">
                
                <!-- Core Settings -->
                <div class="config-group">
                  <h3>Core Settings</h3>
                  <div class="field-wrapper">
                    <label>Project Flag</label>
                    <select 
                      class="dropdown-item"
                      value={data.config?.projectFlag || 'sm'}
                      on:change={(e) => updateConfig('projectFlag', e.target.value)}
                      disabled={isReorderMode}
                    >
                      <option value="sm">sm</option>
                      <option value="understory">understory</option>
                    </select>
                  </div>
                  
                  <label class="checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={data.config?.publicProject === true}
                      on:change={(e) => updateConfig('publicProject', e.target.checked)}
                      disabled={isReorderMode}
                    />
                    Public Project
                  </label>
                  
                  <label class="checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={data.config?.published === true}
                      on:change={(e) => updateConfig('published', e.target.checked)}
                      disabled={isReorderMode}
                    />
                    Published
                  </label>
                  
                  <label class="checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={data.config?.updateable === true}
                      on:change={(e) => updateConfig('updateable', e.target.checked)}
                      disabled={isReorderMode}
                    />
                    Updateable
                  </label>
                </div>
                
                <!-- Features -->
                <div class="config-group">
                  <h3>Features</h3>
                  <div class="checkbox-group">
                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.timeline === true}
                        on:change={(e) => updateConfig('timeline', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Timeline
                    </label>

                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.tour === true}
                        on:change={(e) => updateConfig('tour', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Tour
                    </label>

                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.workspace === true}
                        on:change={(e) => updateConfig('workspace', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Workspace
                    </label>

                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.explore === true}
                        on:change={(e) => updateConfig('explore', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Explore
                    </label>
                    
                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.mapOn === true}
                        on:change={(e) => updateConfig('mapOn', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Map View
                    </label>
                  </div>
                </div>
                
                <!-- Data Structure -->
                <div class="config-group">
                  <h3>Data Structure</h3>
                  <div class="checkbox-group">
                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.multiAnswers === true}
                        on:change={(e) => updateConfig('multiAnswers', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Multi Answers
                    </label>
                    
                    <label class="checkbox-item">
                      <input 
                        type="checkbox" 
                        checked={data.config?.multiRings === true}
                        on:change={(e) => updateConfig('multiRings', e.target.checked)}
                        disabled={isReorderMode}
                      />
                      Multi Rings
                    </label>
                  </div>
                </div>

                <!-- Map Configuration -->
                <div class="config-group">
                  <h3>Map Configuration</h3>
                  <div class="map-controls">
                    {#if data && data.map !== undefined}
                      <div class="map-info">
                        <p><strong>Map Location:</strong></p>
                        <p>Lat: {data?.map.lat}, Long: {data?.map?.long}</p>
                        <p>Radius: {data?.map?.radiusKm}km</p>
                      </div>
                      <button class="btn btn-secondary" on:click={showMapEditor} disabled={isReorderMode}>
                        Edit Map
                      </button>
                      <button class="btn btn-warning" on:click={toggleMapInQuestionset} disabled={isReorderMode}>
                        Remove Map
                      </button>
                    {:else}
                      <p>No map configured</p>
                      <button class="btn btn-primary" on:click={toggleMapInQuestionset} disabled={isReorderMode}>
                        Create Map Questionset
                      </button>
                    {/if}
                  </div>
                </div>

                <!-- Multi Maps Configuration -->
                <div class="config-group">
                  <h3>Multi Maps</h3>
                  <MultiMapsEditor 
                    multiMaps={data.config?.multiMaps || []}
                    {updateConfig}
                  />
                </div>
                
                <div class="config-group">
                  <h3>Visual Styling</h3>
                  <StylingConfigEditor 
                    styling={data.config?.styling || {}}
                    {updateConfig}
                  />
                </div>
                
                <!-- Exit Set Configuration -->
                <div class="config-group">
                  <h3>Exit Set</h3>
                  <ExitSetEditor 
                    exitSet={data.config?.exitSet}
                    {updateConfig}
                    questionSetData={data}
                  />
                </div>

                <!-- Deletion Relationships Configuration -->
                <div class="config-group">
                  <h3>Deletion Relationships</h3>
                  <DelRelDisplay 
                    delRel={data.config?.delRel || []}
                    questions={data.questions || []}
                    {recalculateDelRel}
                  />
                </div>
                
                <!-- Advanced Config Info -->
                <div class="config-group">
                  <h3>Advanced Configuration</h3>
                  <div class="config-info">
                    <p>Translations: {Object.keys(data.config?.translations || {}).length} entries</p>
                    <p>Filter Actions: {(data.config?.filterActions || []).length} configured</p>
                    <p>Link Actions: {(data.config?.linkActions || []).length} configured</p>
                    {#if data.config?.extraMarkup}
                      <p>Custom styling: Present</p>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          {/if}
        </div>
        
        <!-- Reorder Mode Notice -->
        {#if isReorderMode}
          <div class="reorder-notice">
            <h3>Reorder Mode Active</h3>
            <p>Drag and drop items to reorder. Intro and outro sections cannot be moved.</p>
            <div class="reorder-actions">
              <button class="btn btn-primary" on:click={saveReorder}>Save Order</button>
              <button class="btn" on:click={cancelReorder}>Cancel</button>
            </div>
          </div>
        {/if}
        
        <!-- Content Flow -->
        <div class="content-area">
          {#each orderedContent as item, index}
            {#if item.type === 'section'}
              <div 
                class="section-item"
                class:drag-over={dragOverIndex === index}
                class:no-drag={isReorderMode && (item.id === 'intro' || item.id === 'outro')}
                draggable={isReorderMode && (item.id !== 'intro' && item.id !== 'outro')}
                on:dragstart={(e) => handleDragStart(e, item, index)}
                on:dragover={(e) => handleDragOver(e, index)}
                on:dragleave={handleDragLeave}
                on:drop={(e) => handleDrop(e, index)}
              >
                <div class="section-header">
                  <div class="item-info">
                    <span class="tag tag-section">SECTION</span>
                    <strong>{item.id}</strong>
                    {#if isReorderMode && (item.id === 'intro' || item.id === 'outro')}
                      <span class="no-move-indicator">Fixed Position</span>
                    {/if}
                  </div>
                  {#if !isReorderMode}
                    <div class="item-actions">
                      <button class="btn" on:click={() => editSection(item.id)}>Edit</button>
                      <button class="btn is-danger" on:click={() => removeSection(item.id)}>Delete</button>
                    </div>
                  {/if}
                </div>
                <div class="item-content">
                  <h3>{item.data.title || 'Untitled Section'}</h3>
                  <p>{item.data.text || 'No content'}</p>
                </div>
              </div>
            {:else if item.type === 'question'}
              <div 
                class="question-item"
                class:drag-over={dragOverIndex === index}
                draggable={isReorderMode}
                on:dragstart={(e) => handleDragStart(e, item, index)}
                on:dragover={(e) => handleDragOver(e, index)}
                on:dragleave={handleDragLeave}
                on:drop={(e) => handleDrop(e, index)}
              >
                <div class="question-header" class:props-question={isPropsQuestion(item.data)}>
                  <div class="item-info">
                    <span class="tag tag-question">{item.data.kind.toUpperCase()}</span>
                    <strong>{item.data.id}</strong>
                    <span class="relationship-indicator">{getRelationTypeFromDataset(item.data.dataset)}</span>
                    {#if item.data.tourText}
                      <span class="tour-indicator">TOUR</span>
                    {/if}
                  </div>
                  {#if !isReorderMode}
                    <div class="item-actions">
                      <button class="btn" on:click={() => editQuestion(item.index)}>Edit</button>
                      <button class="btn is-danger" on:click={() => removeQuestion(item.index)}>Delete</button>
                    </div>
                  {/if}
                </div>
                <div class="item-content">
                  <h3>{item.data.label}</h3>
                  <p class="question-meta">{getQuestionSummary(item.data)}</p>
                  {#if item.data.tourText}
                    <p class="tour-text"><strong>Tour:</strong> {item.data.tourText}</p>
                  {/if}
                </div>
              </div>
            {/if}
          {/each}
        </div>
      </div>
      
      <!-- Sidebar -->
      <div class="sidebar">
        
        <!-- Control Panel -->
        <div class="controls-panel">
          <h3>Content Controls</h3>
          
          {#if !isReorderMode}
            <div class="control-group">
              <h4>Add Content</h4>
              <button class="btn btn-secondary full-width" on:click={addSection}>+ Add Section</button>
              <button class="btn btn-primary full-width" on:click={addQuestion}>+ Add Question</button>
            </div>
            
            <div class="control-group">
              <h4>Organisation</h4>
              <button class="btn full-width" on:click={enterReorderMode}>Reorder Items</button>
            </div>
            
            <div class="control-group">
              <h4>Save & Validate</h4>
              <button class="btn btn-primary full-width" on:click={saveChanges}>Save Changes</button>
              <div class="save-status" class:is-error={saveStatus.type === 'error'}>
                <strong>Status:</strong> {saveStatus.message}<br/>
                <span class="timestamp">Last saved: {saveStatus.timestamp}</span>
              </div>
            </div>
          {:else}
            <div class="control-group">
              <h4>Reorder Mode</h4>
              <p class="reorder-help">Drag and drop items to reorder them. Intro and outro sections are fixed.</p>
              <button class="btn btn-primary full-width" on:click={saveReorder}>Save New Order</button>
              <button class="btn full-width" on:click={cancelReorder}>Cancel Reorder</button>
            </div>
          {/if}
        </div>
        
        <!-- Map Preview -->
         <!--hiding while testing so not test all at once-->
       <!--<div class="map-preview">
          <h3>Map Preview</h3>
          <div class="preview-note">
            <strong>Single User Response Visualisation</strong><br/>
            Shows how the relationship map would look<br/>
            when one user answers all questions
          </div>
          
          <div class="preview-visualization">
            <QuestionSetVisualiser questionSet={isReorderMode ? reorderData : data}/>
          </div>
        </div>-->
      </div>
    {/if}
  </div>
</div>

<!-- Modals -->
{#if showAddQuestionModal}
  <AddQuestionModal 
    on:close={() => showAddQuestionModal = false}
    on:createDraft={handleCreateDraft}
  />
{/if}

{#if showAddSectionModal}
  <SectionEditModal 
    mode="add"
    on:close={() => showAddSectionModal = false}
    on:save={(e) => handleAddSection(e.detail.id, e.detail.title, e.detail.text)}
  />
{/if}

{#if showSectionEditModal && editingSectionId}
  <SectionEditModal 
    mode="edit"
    sectionId={editingSectionId}
    section={data.sections?.[editingSectionId]}
    on:close={() => showSectionEditModal = false}
    on:save={(e) => handleUpdateSection(editingSectionId, e.detail.title, e.detail.text)}
  />
{/if}

{#if showMapSelector}
  <MapSelector
    on:close={() => showMapSelector = false}
    on:save={handleMapSave}
  />
{/if}

{#if selectedItem?.type === 'question' && !isReorderMode}
  {#if selectedItem.isDraft && draftQuestion}
    <QuestionEditPanel 
      question={draftQuestion}
      questionIndex={data.questions.length}
      isDraft={true}
      on:close={handleCancelDraft}
      on:save={handleSaveDraft}
      on:cancelDraft={handleCancelDraft}
    />
  {:else}
    <QuestionEditPanel 
      question={data.questions[selectedItem.index]}
      questionIndex={selectedItem.index}
      isDraft={false}
      on:close={() => selectedItem = null}
      on:save={handleUpdateQuestion}
    />
  {/if}
{/if}

<style lang="scss">
  .questionset-editor {
    display: grid;
    grid-template-columns: 75% 25%;
    gap: 1px;
    background: #d4d3bc;
    height: calc(100vh - 40px);
  }

  .loading-state {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f6f6f6;
    
    h2 {
      color: #6b7280;
    }
  }
  
  .main-content {
    background: #f6f6f6;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
  
  .sidebar {
    background: #f6f6f6;
    display: flex;
    flex-direction: column;
  }
  
  .global-config {
    background: #f3f2e3;
    border-bottom: 4px solid #d4d3bc;
    transition: all 0.3s ease;
    
    &.collapsed {
      .config-content {
        display: none;
      }
    }
    
    .config-header {
      padding: 20px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      user-select: none;
      
      &:hover {
        background: rgba(0, 0, 0, 0.02);
      }
      
      .config-title {
        flex: 1;
        
        h1 {
          margin: 0 0 5px 0;
          font-size: 1.5rem;
        }
        
        .config-summary {
          font-size: 0.8rem;
          color: #6b7280;
          font-style: italic;
        }
      }
      
      .toggle-btn {
        background: none;
        border: none;
        font-size: 1.2rem;
        color: #6b7280;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        transition: all 0.2s ease;
        
        &:hover {
          background: rgba(0, 0, 0, 0.1);
          color: #374151;
        }
        
        &.expanded {
          transform: rotate(0deg);
        }
      }
    }
    
    .config-content {
      padding: 0 20px 20px 20px;
      animation: slideDown 0.3s ease;
    }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }
  
  .config-basic {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
  }
  
  .config-group {
    background: #f6f6f6;
    padding: 15px;
    border-radius: 10px;
    
    h3 {
      margin-bottom: 10px;
    }
  }
  
  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .checkbox-item {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    cursor: pointer;
    
    input {
      margin-right: 8px;
      width: auto;
    }
  }

  .field-wrapper{
    input{
      background-color: #fff;
      border-radius: 5px;
    }
  }

  .dropdown-item{
    padding: 0px;
    background-color: #fff;
    border-radius: 5px;
    border: 1px solid #d4d3bc;
    width: 100%;
  }

  .config-info {
    font-size: 0.75rem;
    color: #6b7280;
    
    p {
      margin: 4px 0;
    }
  }

  .map-controls {
    display: flex;
    flex-direction: column;
    gap: 10px;
    
    .map-info {
      background: #f0f9ff;
      padding: 8px;
      border-radius: 5px;
      border: 1px solid #0ea5e9;
      font-size: 0.75rem;
      
      p {
        margin: 2px 0;
      }
    }
  }

  // Reorder mode styles
  .reorder-notice {
    background: #fef3c7;
    border: 2px solid #f59e0b;
    padding: 15px;
    margin: 0 20px 20px 20px;
    border-radius: 10px;
    
    h3 {
      margin-bottom: 8px;
      color: #92400e;
    }
    
    p {
      margin-bottom: 10px;
      color: #92400e;
    }
    
    .reorder-actions {
      display: flex;
      gap: 10px;
    }
  }
  
  .content-area {
    flex: 1;
    padding: 20px;
    overflow: auto;
  }
  
  .section-item, .question-item {
    margin-bottom: 20px;
    border: 4px solid #d4d3bc;
    border-radius: 0;
    overflow: hidden;
    cursor: default;
    
    &[draggable="true"] {
      cursor: grab;
      
      &:active {
        cursor: grabbing;
      }
    }
    
    &.drag-over {
      border-color: #3b82f6;
      background: #eff6ff;
    }
    
    &.no-drag {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  .section-header {
    background: #fbbf24;
    padding: 15px;
    border-bottom: 4px solid #d4d3bc;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .question-header {
    background: #60a5fa;
    padding: 15px;
    border-bottom: 4px solid #d4d3bc;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    // Props question styling
    &.props-question {
      background: #a78bfa; // Purple color for props questions
    }
  }
  
  .item-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .item-actions {
    display: flex;
    gap: 4px;
  }
  
  .item-content {
    padding: 15px;
    background: #f6f6f6;
    
    h3 {
      margin-bottom: 8px;
    }
    
    p {
      font-size: 0.75rem;
      color: #464b4b;
      margin: 0;
      margin-bottom: 4px;
    }
  }
  
  .tag {
    background: #e5e7eb;
    padding: 4px 8px;
    border-radius: 0;
    font-size: 11px;
    font-weight: bold;
  }
  
  .tag-section {
    background: #92400e;
    color: #fbbf24;
  }
  
  .tag-question {
    background: #1e40af;
    color: #60a5fa;
  }
  
  .relationship-indicator {
    font-size: 11px;
    background: #e5e7eb;
    padding: 2px 6px;
    border-radius: 0;
    color: #6b7280;
  }
  
  .tour-indicator {
    font-size: 11px;
    background: #10b981;
    padding: 2px 6px;
    border-radius: 0;
    color: white;
  }
  
  .no-move-indicator {
    font-size: 11px;
    background: #fca5a5;
    padding: 2px 6px;
    border-radius: 0;
    color: #991b1b;
  }
  
  .question-meta {
    font-size: 0.75rem !important;
    color: #464b4b !important;
  }
  
  .tour-text {
    font-size: 0.75rem !important;
    color: #10b981 !important;
    font-style: italic;
  }
  
  .controls-panel {
    padding: 20px;
    border-bottom: 4px solid #d4d3bc;
    background:  #f3f2e3;
    
    h3 {
      margin-bottom: 15px;
    }
  }
  
  .control-group {
    margin-bottom: 15px;
    
    h4 {
      font-size: 0.75rem;
      margin-bottom: 8px;
      font-weight: bold;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .reorder-help {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 10px;
  }
  
  .btn {
    padding: 8px 12px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    transition: all 0.2s ease;
    
    &.full-width {
      width: 100%;
      margin-bottom: 6px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    &.btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-warning {
      background: #f59e0b;
      color: white;
      
      &:hover {
        background: #d97706;
      }
    }
    
    &.is-danger {
      background: #fc72cd;
      color: white;
      
      &:hover {
        background: #fc72cdd9;
      }
    }
  }
  
  .save-status {
    padding: 8px;
    background: #f0f9ff;
    border: 1px solid #0ea5e9;
    border-radius: 10px;
    font-size: 11px;
    
    &.is-error {
      background: #fef2f2;
      border-color: #f87171;
    }
    
    .timestamp {
      color: #6b7280;
    }
  }
  
  .map-preview {
    padding: 20px;
    background: #e3e2d3;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    
    h3 {
      margin-bottom: 15px;
    }
  }
  
  .preview-note {
    background: #fef2f2;
    border: 1px dashed #f87171;
    padding: 10px;
    border-radius: 10px;
    font-size: 12px;
    color: #7f1d1d;
    text-align: center;
    margin-bottom: 20px;
  }
  
  .preview-visualization {
    text-align: center;
    width: 100%;
  }
</style>